import { formatUnits } from 'viem';
import { SignTypedDataMutateAsync } from 'wagmi/query';

import { TokenUsdPrices } from '@/store/trade';

import { Quote } from '@/utils/0x.types';
import { TokenResult } from '@/utils/0x/token-registry.types';

import {
  GaslessApprovalTypes,
  GaslessTypes,
  GaslessSubmitApproval,
  GaslessSubmitTrade,
} from '../../../../utils/gasless/types';
import { EIP712TypedData, SignatureType, splitSignature } from '../../../../utils/signature';

export const USE_GAS_ORACLE_DEFAULT_CONFIG = {
  refreshInterval: 0, // disable polling
  revalidateOnFocus: false,
  revalidateIfStale: false,
  revalidateOnReconnect: false,
  revalidateOnMount: true,
  dedupingInterval: 0, // disable deduping
} as const;

interface SignGaslessApprovalParams {
  type: GaslessApprovalTypes;
  eip712: EIP712TypedData;
  signTypedDataAsync: SignTypedDataMutateAsync;
}

export async function signGaslessApproval({
  type,
  eip712,
  signTypedDataAsync,
}: SignGaslessApprovalParams): Promise<GaslessSubmitApproval> {
  const signature = await signTypedDataAsync(eip712);
  const splitSig = splitSignature(signature);

  const signedApproval: GaslessSubmitApproval = {
    type,
    eip712,
    signature: {
      ...splitSig,
      v: Number(splitSig.v),
      signatureType: SignatureType.EIP712,
    },
  };

  return signedApproval;
}

interface SignGaslessTradeParams {
  type: GaslessTypes | 'settler_metatransaction';
  eip712: EIP712TypedData;
  signTypedDataAsync: SignTypedDataMutateAsync;
}

export async function signGaslessTrade({
  type,
  eip712,
  signTypedDataAsync,
}: SignGaslessTradeParams) {
  const signature = await signTypedDataAsync(eip712);
  const splitSig = splitSignature(signature);

  const signedTrade: GaslessSubmitTrade = {
    type,
    eip712,
    signature: {
      ...splitSig,
      v: Number(splitSig.v),
      signatureType: SignatureType.EIP712,
    },
  };

  return signedTrade;
}

export const amountToUsd = (amount: string, decimals: number, usdPrice: number) =>
  Number(formatUnits(BigInt(amount), decimals)) * usdPrice;

export function calculateMatchaAutoFeesInUsdForQuote(
  currentQuote: Quote,
  sellToken: TokenResult,
  buyToken: TokenResult,
  tokenUsdPrices: TokenUsdPrices,
): number {
  if (!currentQuote.fees?.zeroExFee) return 0;
  const feeTokenInfo =
    currentQuote.fees.zeroExFee.token === sellToken?.address
      ? { token: sellToken, usdPrice: tokenUsdPrices.sellTokenUsd }
      : { token: buyToken, usdPrice: tokenUsdPrices.buyTokenUsd };
  const formattedFeeAmount = feeTokenInfo?.token.decimals
    ? parseFloat(
        formatUnits(BigInt(currentQuote.fees.zeroExFee.amount), feeTokenInfo?.token.decimals),
      )
    : 0;
  return formattedFeeAmount * feeTokenInfo.usdPrice;
}
